/**
 * The first screen of a new room: what it is about, what to find out, and
 * anything that narrows it - then one Start.
 *
 * A blank chat box asked a person to know what to type. This writes the room's
 * first message for them and puts each ticked question's table on the board;
 * the chat carries on from there as if they had typed it. The questions live
 * in `startQuestions.ts`.
 */
import { useState } from 'react';
import { api } from '../../services/api';
import {
  PURPOSES,
  SIZE_WORDS,
  START_QUESTIONS,
  STORY_DEFAULT_PICKS,
  STORY_QUESTIONS,
  startPlan,
  storyPlan,
  type CustomerSize,
  type Purpose,
} from './startQuestions';

const CARD = 'rounded-lg border border-gray-200 p-3 dark:border-gray-700';
const INPUT =
  'min-w-0 flex-1 rounded border border-gray-300 px-2 py-1 text-sm dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100';
const LABEL = 'w-14 shrink-0 text-xs text-gray-500 dark:text-gray-400';

const TAB = 'rounded px-2 py-0.5 text-xs';

/** A room about a story, a person or a trend: the reporter's questions, all
 *  ticked to start. Sets the room's kind to topic, which seats the story seats
 *  and swaps in the story tables, then says the first message. */
function StoryStart({
  boardroomId,
  onSay,
  onTables,
}: {
  boardroomId: number;
  onSay: (text: string) => void;
  onTables?: () => void;
}) {
  const [about, setAbout] = useState('');
  const [picked, setPicked] = useState<string[]>(STORY_DEFAULT_PICKS);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const plan = storyPlan(about, picked);
  const toggle = (key: string) => setPicked(p => (p.includes(key) ? p.filter(k => k !== key) : [...p, key]));
  const start = async () => {
    if (!plan || busy) return;
    setBusy(true);
    setError('');
    try {
      await api.updateBoardroom(boardroomId, { room_kind: 'topic' });
      for (const kind of plan.tables) {
        await api.addBoardroomDeliverable(boardroomId, { kind, pinned: true });
      }
      onTables?.();
      onSay(plan.line);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not start the room');
    } finally {
      setBusy(false);
    }
  };
  return (
    <form
      className="flex flex-col gap-3"
      data-testid="story-start"
      onSubmit={e => {
        e.preventDefault();
        void start();
      }}
    >
      <label className="flex items-center gap-2">
        <span className="shrink-0 text-sm font-medium text-gray-900 dark:text-gray-100">What story?</span>
        <input
          value={about}
          onChange={e => setAbout(e.target.value)}
          placeholder="Lori Loughlin and John Stamos, a person, or a trend"
          className={INPUT}
          aria-label="What story"
        />
      </label>
      <fieldset>
        <legend className="mb-1 text-xs text-gray-500 dark:text-gray-400">Find out:</legend>
        <div className="grid gap-1 sm:grid-cols-2">
          {STORY_QUESTIONS.map(q => (
            <label key={q.key} className="inline-flex items-center gap-1.5 text-sm text-gray-700 dark:text-gray-200">
              <input type="checkbox" checked={picked.includes(q.key)} onChange={() => toggle(q.key)} />
              {q.label}
            </label>
          ))}
        </div>
      </fieldset>
      {plan && (
        <p className="text-xs text-gray-500 dark:text-gray-400" data-testid="story-line">
          The room will be asked: “{plan.line}”
        </p>
      )}
      {error && (
        <p className="text-xs text-red-600 dark:text-red-400" role="alert">
          {error}
        </p>
      )}
      <div className="flex justify-end">
        <button
          type="submit"
          disabled={!plan || busy}
          className="rounded bg-indigo-600 px-4 py-1.5 text-sm text-white hover:bg-indigo-700 disabled:opacity-50 dark:bg-indigo-500"
        >
          Start
        </button>
      </div>
    </form>
  );
}

export default function StartForm(props: {
  boardroomId: number;
  onSay: (text: string) => void;
  onTables?: () => void;
}) {
  // A MARKET OR A STORY: two different sets of questions, chosen first.
  const [mode, setMode] = useState<'market' | 'story'>('market');
  return (
    <div className={`${CARD} flex flex-col gap-3`}>
      <div className="flex items-center gap-1" data-testid="start-mode">
        <span className="mr-1 text-xs text-gray-500 dark:text-gray-400">Researching</span>
        {(
          [
            ['market', 'a market or company'],
            ['story', 'a story, person or trend'],
          ] as const
        ).map(([m, label]) => (
          <button
            key={m}
            type="button"
            onClick={() => setMode(m)}
            className={`${TAB} ${
              mode === m ? 'bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-100' : 'text-gray-500'
            }`}
          >
            {label}
          </button>
        ))}
      </div>
      {mode === 'story' ? <StoryStart {...props} /> : <MarketStart {...props} />}
    </div>
  );
}

function MarketStart({
  boardroomId,
  onSay,
  onTables,
}: {
  boardroomId: number;
  /** Send this as the room's first chat message. */
  onSay: (text: string) => void;
  /** The board's tables changed: reload them. */
  onTables?: () => void;
}) {
  const [about, setAbout] = useState('');
  const [picked, setPicked] = useState<string[]>([]);
  const [audience, setAudience] = useState('');
  const [where, setWhere] = useState('');
  const [size, setSize] = useState<CustomerSize>('any');
  const [why, setWhy] = useState<Purpose>('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const plan = startPlan({ about, picked, audience, where, size, why });
  const toggle = (key: string) => setPicked(p => (p.includes(key) ? p.filter(k => k !== key) : [...p, key]));

  const start = async () => {
    if (!plan || busy) return;
    setBusy(true);
    setError('');
    try {
      if (plan.company) {
        await api.createBoardroomItem(boardroomId, {
          kind: 'COMPANY',
          title: plan.company.domain,
          payload: { url: plan.company.url },
        });
      }
      // One at a time: each add places the table after the last one.
      for (const kind of plan.tables) {
        await api.addBoardroomDeliverable(boardroomId, { kind, pinned: true });
      }
      onTables?.();
      onSay(plan.line);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not start the room');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      className="flex flex-col gap-3"
      data-testid="start-form"
      onSubmit={e => {
        e.preventDefault();
        void start();
      }}
    >
      <label className="flex items-center gap-2">
        <span className="shrink-0 text-sm font-medium text-gray-900 dark:text-gray-100">What is it about?</span>
        <input
          value={about}
          onChange={e => setAbout(e.target.value)}
          placeholder="practice software, or a company's website"
          className={INPUT}
          aria-label="What is it about"
        />
      </label>

      <fieldset>
        <legend className="mb-1 text-xs text-gray-500 dark:text-gray-400">
          Besides {plan?.company ? 'who competes with it' : 'who sells it'}, find out (pick any):
        </legend>
        <div className="grid gap-1 sm:grid-cols-2">
          {START_QUESTIONS.map(q => (
            <label key={q.key} className="inline-flex items-center gap-1.5 text-sm text-gray-700 dark:text-gray-200">
              <input type="checkbox" checked={picked.includes(q.key)} onChange={() => toggle(q.key)} />
              {q.label}
            </label>
          ))}
        </div>
      </fieldset>

      <fieldset className="flex flex-col gap-1.5">
        <legend className="mb-1 text-xs text-gray-500 dark:text-gray-400">Narrow it (optional):</legend>
        <label className="flex items-center gap-2">
          <span className={LABEL}>For</span>
          <input
            value={audience}
            onChange={e => setAudience(e.target.value)}
            placeholder="mental health practices"
            className={INPUT}
          />
        </label>
        <label className="flex items-center gap-2">
          <span className={LABEL}>Where</span>
          <input
            value={where}
            onChange={e => setWhere(e.target.value)}
            placeholder="the United States"
            className={INPUT}
          />
        </label>
        <div className="flex items-center gap-2">
          <span className={LABEL}>Size</span>
          {(Object.keys(SIZE_WORDS) as CustomerSize[]).map(s => (
            <label key={s} className="inline-flex items-center gap-1 text-sm text-gray-700 dark:text-gray-200">
              <input type="radio" name="start-size" checked={size === s} onChange={() => setSize(s)} />
              {SIZE_WORDS[s]}
            </label>
          ))}
        </div>
      </fieldset>

      <fieldset>
        <legend className="mb-1 text-xs text-gray-500 dark:text-gray-400">Why do you want to know? (optional)</legend>
        <div className="flex flex-wrap gap-x-3 gap-y-1">
          {PURPOSES.map(p => (
            <label key={p.key} className="inline-flex items-center gap-1 text-sm text-gray-700 dark:text-gray-200">
              <input
                type="radio"
                name="start-why"
                checked={why === p.key}
                onChange={() => setWhy(p.key)}
                onClick={() => why === p.key && setWhy('')}
              />
              {p.label}
            </label>
          ))}
        </div>
      </fieldset>

      {plan && (
        <p className="text-xs text-gray-500 dark:text-gray-400" data-testid="start-line">
          The room will be asked: “{plan.line}”
        </p>
      )}
      {error && (
        <p className="text-xs text-red-600 dark:text-red-400" role="alert">
          {error}
        </p>
      )}
      <div className="flex justify-end">
        <button
          type="submit"
          disabled={!plan || busy}
          className="rounded bg-indigo-600 px-4 py-1.5 text-sm text-white hover:bg-indigo-700 disabled:opacity-50 dark:bg-indigo-500"
        >
          Start
        </button>
      </div>
    </form>
  );
}
